import { Navigate, Outlet } from 'react-router-dom';
import type { ReactNode } from 'react';

import { HOME } from '../layout/navigation';

/*
 * `./AuthContext`, not `./AuthProvider`.
 *
 * Two auth contexts exist in this tree and only one is mounted: `App.tsx`
 * wraps the application in `AuthContext`'s provider, and nothing imports
 * `AuthProvider.tsx` at all. This component read the unmounted one, so the
 * first route that used it threw "useAuth must be used within AuthProvider"
 * rather than rendering - which nothing noticed, because no route used it until
 * phase 9's manager screens.
 *
 * Reconciling the two providers belongs to whoever owns ACCESS (STATE open
 * item 6). Until then this points at the live one.
 */
import { useAuth } from './AuthContext';
import { FullPageSpinner } from '../ui/FullPageSpinner';

/**
 * FND-7.2 / uml/17-project-scaffolding.md S17.
 *
 * **RequirePermission hides. It never authorises.**
 *
 * That sentence is the whole contract. A user who deletes this component from
 * the bundle, or appends a code to the permissions array in the console, gets
 * to the screen and then gets a 403 from every endpoint it calls, because
 * `AccessService.hasPermission(user, code)` is called INSIDE the service
 * (document 18 section 2). The controller decorator is convenience; the service
 * call is the enforcement. A test calling the service directly must still be
 * refused.
 *
 * What this buys is a menu with no links that lead to a 403.
 */

/** Route form: wraps a branch of the route table. */
export function RequirePermission({ code }: { code: string }): JSX.Element {
  const { user, loading } = useAuth();

  if (loading) return <FullPageSpinner />;
  if (!user?.permissions.includes(code)) return <Navigate to={HOME} replace />;

  return <Outlet />;
}

/**
 * Inline form: renders nothing where a control would be. Used for the buttons
 * and navigation links of every feature screen.
 */
export function IfPermitted({
  code,
  children,
  fallback = null,
}: {
  code: string;
  children: ReactNode;
  fallback?: ReactNode;
}): JSX.Element {
  const { user } = useAuth();
  return <>{user?.permissions.includes(code) ? children : fallback}</>;
}
